import React, {
    MutableRefObject, RefObject, useCallback, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';

import COLOR from '@CONSTANTS/COLOR.constant';
import PDF from '@CONSTANTS/PDF.constant';
import { useGetAppSettingsQuery, useGetSystemSettingsQuery } from '@SERVICES';

import getEditorConfig, { EditorContext } from '@HELPERS/getEditorConfig';
import { useTinyMCE } from '@HOOKS/useTinyMCE';
import { useComments } from '@HOOKS/useComments';

import CircularProgressIndicator from '@COMPONENTS/SHARED/CircularProgressIndicator';
import CommentContextMenu from '@COMPONENTS/SHARED/Comments/CommentContextMenu.component';
import CommentThreadPanel from '@COMPONENTS/SHARED/Comments/CommentThreadPanel.component';

import { EditorStyleContent, VariantBodyEditorStyleContent } from '@CONSTANTS/EDITOR_STYPE';
import styles from './BaseEditor.module.scss';

const cx: CX = classnames.bind(styles);

const EDITOR_MARGIN = PDF['pdf-editor-margin'];
const PAGE_HEIGHT = PDF['pdf-page-height'];
const PAGE_WIDTH = PDF['pdf-page-width'];

function BaseEditor(props: Props) {
    const {
        initialValue, isLandscape, onEditorChange, variantKey, context = 'default',
    } = props;

    const baseEditorRef: RefObject<HTMLDivElement> = useRef(null);
    const landscapeLabelRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
    const boundsRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

    const editorRef: MutableRefObject<any> = useRef(null);
    const timer: MutableRefObject<any> = useRef(null);

    const [isEditorReady, setEditorReady] = useState<boolean>(false);

    // Get app settings for dynamic colors and system settings for WYSIWYG configuration
    const { data: appSettings } = useGetAppSettingsQuery();
    const { data: systemSettings } = useGetSystemSettingsQuery();

    // Use the TinyMCE hook to ensure scripts are loaded
    const { isLoaded: isTinyMCELoaded, isLoading: isTinyMCELoading, error: tinyMCEError } = useTinyMCE();

    // Feature flag to temporarily disable commenting
    const ENABLE_COMMENTS = false; // Set to true to re-enable commenting
    
    // Initialize comment functionality (always call hook but disable functionality via flag)
    const {
        threads,
        activeThreadId,
        contextMenu,
        createThread,
        addComment,
        toggleThreadStatus,
        deleteThread,
        handleSelection,
        hideContextMenu,
        wrapSelectionWithComment,
        handleAnnotationClick,
        setActiveThreadId,
        setEditorRef,
        restoreCommentHighlights,
        clearActiveThread,
    } = useComments({
        // You can pass document/section/variant IDs here for persistence
        variantId: variantKey || undefined,
    });

    const editorConfig = getEditorConfig(
        isLandscape ? 'landscape' : 'portrait',
        appSettings,
        systemSettings,
        context as EditorContext,
    );

    function toggleBounds() {
        clearTimeout(timer.current);

        timer.current = setTimeout(() => {
            const { contentAreaContainer } = editorRef.current;

            const { height } = contentAreaContainer.getBoundingClientRect();

            if (height > PAGE_WIDTH + EDITOR_MARGIN) {
                boundsRef.current!.style.visibility = 'visible';
            } else {
                boundsRef.current!.style.visibility = 'hidden';
            }
        }, 150);
    }

    function handleEditorChange(content: string) {
        onEditorChange(content);

        if (isLandscape) {
            toggleBounds();
        }
    }

    const setupEditorElements = useCallback(() => {
        if (landscapeLabelRef.current || boundsRef.current) return;

        const toxSidebarWrap = baseEditorRef.current?.querySelector(
            '.tox-sidebar-wrap',
        ) as HTMLDivElement;
        if (!toxSidebarWrap) return;

        const landscapeLabel = document.createElement('div');
        landscapeLabel.classList.add(cx('landscape'));
        landscapeLabel.innerText = 'Landscape';

        const bounds = document.createElement('div');
        bounds.classList.add(cx('bounds'));

        toxSidebarWrap.appendChild(landscapeLabel);
        toxSidebarWrap.appendChild(bounds);

        landscapeLabelRef.current = landscapeLabel;
        boundsRef.current = bounds;
    }, []);

    const getLayoutWidth = useCallback(() => {
        setupEditorElements();

        if (!editorRef.current) return;

        const { contentAreaContainer, contentWindow } = editorRef.current;
        if (!contentAreaContainer || !contentWindow) return;

        const rootClassList = contentWindow.document.documentElement.classList;
        rootClassList.toggle('landscape', isLandscape);
        rootClassList.toggle('portrait', !isLandscape);

        if (isLandscape) {
            landscapeLabelRef.current!.style.visibility = 'visible';
            toggleBounds();
        } else {
            landscapeLabelRef.current!.style.visibility = 'hidden';
            boundsRef.current!.style.visibility = 'hidden';
        }
    }, [isLandscape, setupEditorElements]);

    useLayoutEffect(() => {
        const width = isLandscape ? PAGE_HEIGHT : PAGE_WIDTH;
        document.documentElement.style.setProperty(
            '--editor-width',
            `${width}px`,
        );
    }, [isLandscape]);

    const cleanFontSizes = (content: string) => content.replace(/font-size:\s*(\d+)pt;/g, (match, size) => {
        const pxSize = `${parseInt(size, 10)}px`; // Convert pt to px
        return `font-size: ${pxSize};`;
    });

    const handleInit = (editor: TinyMCEEditor) => {
        const body = editor.getBody();
        if (body) {
            body.style.fontFamily = 'Arial, sans-serif';
            body.style.fontSize = '12px';
        }
    };

    // Show loading if TinyMCE is not ready yet
    if (isTinyMCELoading || !isTinyMCELoaded) {
        return (
            <div className={cx('editor-loading')}>
                <CircularProgressIndicator
                    color={COLOR['marriott-primary']}
                    size={40}
                    thickness={4}
                />
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                    {isTinyMCELoading ? 'Loading editor...' : 'Initializing editor...'}
                </div>
            </div>
        );
    }

    // Show error if TinyMCE failed to load
    if (tinyMCEError) {
        return (
            <div className={cx('editor-error')} style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ color: 'red', marginBottom: '10px' }}>
                    Editor Load Error
                </div>
                <div style={{ fontSize: '14px' }}>
                    {tinyMCEError}
                </div>
            </div>
        );
    }

    return (
        <>
            <div
                ref={baseEditorRef}
                className={cx('base-editor', {
                    show: isEditorReady,
                    'variant-body-editor': context === 'variant-body',
                })}
            >
                <Editor
                    key={variantKey}
                    onInit={(evt, editor) => {
                        editorRef.current = editor;
                        setEditorReady(true);
                        getLayoutWidth();
                        handleEditorChange(cleanFontSizes(editor.getContent()));
                    }}
                    onEditorChange={(content) => {
                        handleEditorChange(cleanFontSizes(content) || '');
                    }}
                    initialValue={cleanFontSizes(initialValue)}
                    init={{
                        ...editorConfig,

                        height: 700,
                        min_height: 700,
                        paste_as_text: false,
                        paste_data_images: true,
                        paste_word_valid_elements: 'b,strong,i,em,h1,h2,h3',
                        paste_retain_style_properties: 'all',
                        paste_preprocess: (plugin, args) => {
                            // eslint-disable-next-line no-param-reassign
                            args.content = cleanFontSizes(args.content);
                        },
                        toolbar: [
                            'undo redo | styleselect | bold italic underline strikethrough | fontsize blocks fontfamily | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist',
                            'forecolor backcolor removeformat | charmap emoticons | preview print | image media template link anchor | ltr rtl',
                        ],
                        toolbar_sticky: true,
                        toolbar_sticky_offset: 80,
                        browser_spellcheck: false,
                        spellchecker: false,
                        setup: (editor) => {
                            editor.on('init', () => {
                                handleInit(editor);
                                if (ENABLE_COMMENTS) {
                                    setEditorRef(editor);

                                    // Add comment functionality
                                    let selectionTimeout: NodeJS.Timeout;
                                    
                                    // TinyMCE selectionchange event
                                    editor.on('selectionchange', () => {
                                        // Clear any existing timeout
                                        if (selectionTimeout) {
                                            clearTimeout(selectionTimeout);
                                        }
                                        
                                        // Delay to allow selection to stabilize and avoid rapid firing
                                        selectionTimeout = setTimeout(() => {
                                            const { selection } = editor;
                                            if (!selection) return;
                                            
                                            const selectedText = selection.getContent({ format: 'text' }).trim();

                                            if (selectedText.length > 0) {
                                                handleSelection(editor);
                                            } else {
                                                hideContextMenu();
                                            }
                                        }, 200);
                                    });
                                    
                                    // Alternative: Listen to mouseup event on editor body for text selection
                                    editor.on('mouseup', () => {
                                        setTimeout(() => {
                                            const { selection } = editor;
                                            if (!selection) return;
                                            
                                            const selectedText = selection.getContent({ format: 'text' }).trim();
                                            
                                            if (selectedText.length > 0) {
                                                handleSelection(editor);
                                            } else {
                                                hideContextMenu();
                                            }
                                        }, 100);
                                    });

                                    // Handle clicks on comment annotations
                                    editor.on('click', (e) => {
                                        const target = e.target as HTMLElement;
                                        const commentAnnotation = target.closest('.comment-annotation');
                                        if (commentAnnotation) {
                                            const threadId = commentAnnotation.getAttribute('data-comment-id');
                                            if (threadId) {
                                                handleAnnotationClick(threadId);
                                            }
                                        } else {
                                            hideContextMenu();
                                            clearActiveThread();
                                        }
                                    });

                                    // Allow comment spans in valid elements
                                    editor.schema.addValidElements('span[data-comment-id|class|title]');
                                    
                                    // Restore comment highlights after content is loaded
                                    editor.on('LoadContent', () => {
                                        setTimeout(() => restoreCommentHighlights(editor), 100);
                                    });
                                    
                                    // Also restore highlights after initial content load
                                    setTimeout(() => restoreCommentHighlights(editor), 200);
                                }
                            });

                            const style = document.createElement('style');
                            style.textContent = context === 'variant-body' ? VariantBodyEditorStyleContent : EditorStyleContent;
                            document.head.appendChild(style);
                        },
                    }}
                />
            </div>
            {!isEditorReady && (
                <div className={cx('editor-loading')}>
                    <CircularProgressIndicator
                        color={COLOR['marriott-primary']}
                        size={40}
                        thickness={4}
                    />
                </div>
            )}

            {/* Comment UI Components - Temporarily Disabled */}
            {ENABLE_COMMENTS && (
                <>
                    <CommentContextMenu
                        position={contextMenu}
                        onCreateComment={(selection, commentText) => {
                            const thread = createThread(selection, commentText);
                            wrapSelectionWithComment(thread.id, selection);
                        }}
                        onClose={hideContextMenu}
                    />

                    {activeThreadId && threads.find(t => t.id === activeThreadId) && (
                        <div
                            style={{
                                position: 'fixed',
                                right: '20px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 10000,
                            }}
                        >
                            <CommentThreadPanel
                                thread={threads.find(t => t.id === activeThreadId)!}
                                onAddComment={addComment}
                                onToggleStatus={toggleThreadStatus}
                                onDeleteThread={deleteThread}
                                onClose={clearActiveThread}
                            />
                        </div>
                    )}
                </>
            )}
        </>
    );
}

BaseEditor.defaultProps = {
    variantKey: '',
    context: 'default',
};

BaseEditor.propTypes = {
    initialValue: PropTypes.string.isRequired,
    variantKey: PropTypes.string,
    isLandscape: PropTypes.bool.isRequired,
    onEditorChange: PropTypes.func.isRequired,
    context: PropTypes.oneOf(['default', 'variant-body']),
};

type Props = InferProps<typeof BaseEditor.propTypes>;

export default BaseEditor;
